import { type } from "arktype";
import { RooDate } from "roo-time";
/**
 * Per-user push-notification preferences (stored in `user_app.notification_preferences_raw`).
 *
 * Only the **card-processing reminder** is user-configurable. Support-chat reply notifications are
 * transactional (the user is waiting on an answer) and are deliberately NOT represented here — they
 * are always on; the only way to silence them is the OS-level all-off.
 *
 * A NULL column reads as {@link createDefaultNotificationPreferences} via {@link withNotificationDefaults},
 * so existing users need no backfill and are never silently opted out.
 *
 * NOTE: this is a plain-object arktype schema (no `.or()`), so it is safe under the mobile arktype
 * Hermes stub — though validation is server-only regardless. The normalizers below are plain JS (never
 * invoke arktype at runtime) so they work identically on mobile, where arktype is a no-op stub.
 */
export const notificationPreferencesSchema = type({
    cardReminders: {
        /** Master on/off for the "you have transactions to process" reminder. */
        enabled: "boolean",
        /**
         * Hours (0–23) in the user's `communication_timezone` at which the reminder may fire. A LIST, not
         * fixed slots: 0–{@link MAX_SEND_HOURS_PER_DAY} entries, deduped, sorted ascending. **Empty is a
         * valid state** — reminders stay enabled but nothing fires (no hour ever matches); the settings UI
         * lets the user clear all times and shows an empty-state note.
         */
        sendHoursLocal: "number[]",
        /** ISO instant; while in the future the reminder cron skips this user ("pause reminders"). */
        "snoozedUntilIso?": "string",
    },
});
/**
 * Max distinct reminder times a user may set per day. Matches today's 3×/day cron ceiling so a power
 * user can recreate the old cadence, but no one can exceed it (turning the reminder into a fire-hose).
 * Confirmed product decision (2026-07-02). Enforced in {@link normalizeSendHoursLocal} (server + UI).
 */
export const MAX_SEND_HOURS_PER_DAY = 3;
/** Default send times: 8 AM + 8 PM local — a morning and an end-of-day nudge to process transactions. */
export const DEFAULT_SEND_HOURS_LOCAL = [8, 20];
/** Fresh, unshared default preferences (safe to mutate). Mirrors the `createEmptyProgress` convention. */
export function createDefaultNotificationPreferences() {
    return { cardReminders: { enabled: true, sendHoursLocal: [...DEFAULT_SEND_HOURS_LOCAL] } };
}
function isRecord(x) {
    return typeof x === "object" && x !== null && !Array.isArray(x);
}
/**
 * Normalize a caller-supplied send-hours list: keep only integers in [0, 23], dedup, sort ascending, and
 * cap at {@link MAX_SEND_HOURS_PER_DAY}. Server-authoritative — never trust the client's array shape.
 *
 * Distinguishes "missing" from "empty": a non-array input (undefined / null / garbage) falls back to the
 * default {@link DEFAULT_SEND_HOURS_LOCAL}; an explicit empty array is preserved (the user cleared all
 * times — a valid, if inert, state the UI otherwise guards against).
 */
export function normalizeSendHoursLocal(input) {
    if (!Array.isArray(input)) {
        return [...DEFAULT_SEND_HOURS_LOCAL];
    }
    const valid = input.filter((h) => typeof h === "number" && Number.isInteger(h) && h >= 0 && h <= 23);
    const deduped = Array.from(new Set(valid)).sort((a, b) => a - b);
    return deduped.slice(0, MAX_SEND_HOURS_PER_DAY);
}
/**
 * Plan adding one hour to a send-hours list, enforcing the cap + no-duplicates. Returns the resulting
 * list and a discriminator the UI uses to give feedback: `added` (persist `next`), `already-present`
 * (no change — the hour is already set), or `at-cap` (no change — already at {@link MAX_SEND_HOURS_PER_DAY}).
 */
export function planSendHourAdd(current, hour, max = MAX_SEND_HOURS_PER_DAY) {
    if (current.includes(hour)) {
        return { next: current, result: "already-present" };
    }
    if (current.length >= max) {
        return { next: current, result: "at-cap" };
    }
    return { next: normalizeSendHoursLocal([...current, hour]), result: "added" };
}
/**
 * Interpret a raw `notification_preferences_raw` value (possibly NULL / partial / malformed) into a
 * complete, valid {@link NotificationPreferences}, applying defaults per-field. This is the read-time
 * defaulter (no backfill) AND the server-side write normalizer — plain JS so it behaves identically on
 * mobile (where arktype is stubbed).
 */
export function withNotificationDefaults(raw) {
    const cr = isRecord(raw) && isRecord(raw["cardReminders"]) ? raw["cardReminders"] : {};
    const enabled = typeof cr["enabled"] === "boolean" ? cr["enabled"] : true;
    const sendHoursLocal = normalizeSendHoursLocal(cr["sendHoursLocal"]);
    // Server-authoritative: keep the snooze ONLY if it parses to a real instant, so a malformed/garbage
    // client string can't persist into the column (consistent with how sendHoursLocal is validated).
    const rawSnooze = cr["snoozedUntilIso"];
    const snoozedUntilIso = typeof rawSnooze === "string" && rawSnooze.length > 0 && !Number.isNaN(new RooDate(rawSnooze).getTime())
        ? rawSnooze
        : undefined;
    return {
        cardReminders: Object.assign({ enabled,
            sendHoursLocal }, (snoozedUntilIso ? { snoozedUntilIso } : {})),
    };
}
/**
 * Whether a snooze is currently active (reminder should be skipped). Shared by the reminder cron (skip
 * decision) and the mobile settings UI (show "paused until…"). An absent / empty / unparseable
 * `snoozedUntilIso` is treated as "not snoozed".
 */
export function isSnoozeActive(p) {
    if (!p.snoozedUntilIso) {
        return false;
    }
    const untilMs = new RooDate(p.snoozedUntilIso).getTime();
    if (Number.isNaN(untilMs)) {
        return false;
    }
    return untilMs > p.nowMs;
}
//# sourceMappingURL=notificationPreferences.models.js.map